import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Seo } from "@/components/Seo";
import { Reveal, Panel, Dot } from "@/components/System";
import { ARCH_LAYERS } from "@/data/content";

const Architecture = () => {
    const [active, setActive] = useState("agentvm");
    const layer = ARCH_LAYERS.find((l) => l.id === active);
    const activeIdx = ARCH_LAYERS.findIndex((l) => l.id === active);

    return (
        <div>
            <Seo path="/architecture" />
            <section className="relative overflow-hidden px-4 pb-16 pt-36 sm:px-6">
                <div className="vm-grid-bg pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />
                <div className="relative mx-auto max-w-7xl">
                    <div className="vm-micro mb-6">AGENTVM://ARCHITECTURE</div>
                    <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-[#F5F9FF] sm:text-6xl">
                        TWELVE LAYERS.
                        <br />
                        <span className="text-[#00D9FF] vm-glow">ONE VERIFIABLE PATH.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B8C7D9] sm:text-lg">
                        The AgentVM system map: from the user and private data down to onchain records and economic
                        action. Select any layer to inspect its role.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6" data-testid="architecture-map">
                <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr]">
                    <div className="relative">
                        <div className="absolute bottom-2 left-[22px] top-2 w-px bg-[rgba(0,217,255,0.14)]" aria-hidden="true" />
                        <div className="flex flex-col gap-1.5">
                            {ARCH_LAYERS.map((l, i) => {
                                const isActive = l.id === active;
                                return (
                                    <button
                                        key={l.id}
                                        data-testid={`arch-${l.id}`}
                                        onClick={() => setActive(l.id)}
                                        aria-pressed={isActive}
                                        className={`group relative flex items-center gap-4 border py-2.5 pl-2 pr-4 text-left transition-all duration-300 ${
                                            isActive
                                                ? "border-[#00D9FF]/55 bg-[#00D9FF]/[0.06]"
                                                : "border-transparent hover:border-[rgba(0,217,255,0.25)] hover:bg-[#080F18]/60"
                                        }`}
                                    >
                                        <span
                                            className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center border font-mono text-[9px] tracking-widest transition-colors ${
                                                isActive
                                                    ? "border-[#00D9FF] bg-[#05070B] text-[#00D9FF]"
                                                    : "border-[rgba(0,217,255,0.25)] bg-[#05070B] text-[#B8C7D9]/60 group-hover:text-[#F5F9FF]"
                                            }`}
                                        >
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className={`font-mono text-xs tracking-[0.2em] sm:text-sm ${isActive ? "text-[#F5F9FF]" : "text-[#B8C7D9]/85 group-hover:text-[#F5F9FF]"}`}>
                                            {l.name}
                                        </span>
                                        {l.id === "agentvm" && (
                                            <span className="vm-micro ml-auto hidden text-[#00D9FF] sm:block">CORE</span>
                                        )}
                                        {isActive && <Dot className="ml-auto" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="lg:sticky lg:top-24 lg:self-start">
                        <Panel title={`ARCH://LAYER/${layer.id.toUpperCase()}`} right={`${String(activeIdx + 1).padStart(2, "0")} / ${ARCH_LAYERS.length}`} className="scanlines" testid="arch-inspector">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={layer.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.3 }}
                                    className="p-6 sm:p-8"
                                >
                                    <h2 className="font-mono text-2xl tracking-[0.14em] text-[#F5F9FF] sm:text-3xl">{layer.name}</h2>
                                    <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#B8C7D9] sm:text-base">{layer.desc}</p>
                                    <div className="mt-8 space-y-2 font-mono text-[10px] tracking-[0.18em] text-[#B8C7D9]/60">
                                        <div className="flex justify-between border-b border-[rgba(0,217,255,0.08)] py-2">
                                            <span>UPSTREAM</span>
                                            <span className="text-[#F5F9FF]/80">{activeIdx > 0 ? ARCH_LAYERS[activeIdx - 1].name : "—"}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-[rgba(0,217,255,0.08)] py-2">
                                            <span>DOWNSTREAM</span>
                                            <span className="text-[#F5F9FF]/80">{activeIdx < ARCH_LAYERS.length - 1 ? ARCH_LAYERS[activeIdx + 1].name : "—"}</span>
                                        </div>
                                        <div className="flex justify-between py-2">
                                            <span>PATH INTEGRITY</span>
                                            <span className="text-emerald-300">CONTINUOUS</span>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </Panel>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Architecture;
